import React, { type ReactNode, useCallback, useMemo, useRef } from 'react';
import {
  Select as SelectRAC,
  Button as ButtonRAC,
  SelectValue as SelectValueRAC,
  Popover as PopoverRAC,
  ListBox as ListBoxRAC,
  ListBoxItem as ListBoxItemRAC,
  TextField as TextFieldRAC,
  Input as InputRAC,
} from 'react-aria-components';

import { useConfig } from '../../contexts/config-context';
import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { type DateInputProps } from '../../fields/date-input/date-input/date-input-props';
import { findChildOfType, applyInternalProps } from '../../services/find-child';
import {
  getDateFormatParts,
  isSupportedDatePart,
  SupportedDateParts,
} from '../../utils/date-format-parts';
import { getDatePartsErrors } from '../date-field/date-field-utils';
import { deserializeDate, serializeDate } from '../date-picker/helpers';
import { getMonthList } from '../date-picker/month-select/helpers';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import {
  Label,
  type LabelInternalProps,
} from '../field-label/field-label-headless';
import { FieldsetHeadless } from '../fieldset/fieldset-headless';

export type DateInputComponentProps = {
  children: ReactNode;
};

export const DateInput = ({ children }: DateInputComponentProps) => {
  const { id, required, onBlur, onFocus, FieldLayout } =
    useFieldPropsV2<DateInputProps>();

  const { inputId, labelId, ariaDescribedBy } = useFieldAttributes();
  const { regionalFormat } = useConfig();

  const dateFormat = useMemo(
    () =>
      getDateFormatParts(regionalFormat)
        .filter(isSupportedDatePart)
        .map((part) => part.toUpperCase() as SupportedDateParts),
    [regionalFormat],
  );

  const labelElement = findChildOfType(children, DateInput.Label);
  const descriptionElement = findChildOfType(children, DateInput.Description);
  const errorElement = findChildOfType(children, DateInput.Error);

  const dateElementsByPart = {
    [SupportedDateParts.MONTH]: findChildOfType(children, DateInput.Month),
    [SupportedDateParts.DAY]: findChildOfType(children, DateInput.Day),
    [SupportedDateParts.YEAR]: findChildOfType(children, DateInput.Year),
  };

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() =>
        applyInternalProps<LabelInternalProps>(labelElement, { as: 'div' })
      }
      renderInput={() => (
        <FieldsetHeadless
          id={inputId}
          ariaLabelledBy={labelId}
          ariaDescribedBy={ariaDescribedBy}
          required={required}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {dateElementsByPart[dateFormat[0]]}
          {dateElementsByPart[dateFormat[1]]}
          {dateElementsByPart[dateFormat[2]]}
        </FieldsetHeadless>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type MonthProps = {
  children: ReactNode;
  className?: string;
};

const Month = ({ children, className }: MonthProps) => {
  const { id, disabled, showDateLabels } = useFieldPropsV2<DateInputProps>();
  const { inputId } = useDateFieldAttributes(SupportedDateParts.MONTH);
  const { partValue, partError, onPartChange, isFirstPart } = useDatePart(
    SupportedDateParts.MONTH,
  );
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleMonthChange = useCallback(
    (selectedValue: React.Key | null) => {
      onPartChange(typeof selectedValue === 'string' ? selectedValue : '');
    },
    [onPartChange],
  );

  useFocusFieldEvent(() => {
    if (isFirstPart) {
      buttonRef.current?.focus();
    }
  }, id);

  const labelElement = findChildOfType(children, Month.Label);
  const optionsElement = findChildOfType(children, Month.Options);

  return (
    <div className={className} data-date-field={true}>
      {showDateLabels ? labelElement : null}
      <SelectRAC
        value={partValue || null}
        onChange={handleMonthChange}
        isDisabled={disabled}
        isInvalid={partError}
      >
        <ButtonRAC
          ref={buttonRef}
          id={inputId}
          data-hook={SupportedDateParts.MONTH}
          data-invalid={partError}
        >
          <SelectValueRAC />
        </ButtonRAC>
        {optionsElement}
      </SelectRAC>
    </div>
  );
};

type MonthLabelProps = {
  className?: string;
};

const MonthLabel = ({ className }: MonthLabelProps) => {
  return (
    <DateLabel datePart={SupportedDateParts.MONTH} className={className} />
  );
};

type MonthOptionsProps = {
  children: ReactNode;
  className?: string;
};

const MonthOptions = ({ children, className }: MonthOptionsProps) => {
  const { language } = useConfig();

  const months = useMemo(() => getMonthList(language), [language]);

  const optionElement = findChildOfType(children, MonthOptions.Option) ?? (
    <MonthOptions.Option />
  );

  return (
    <PopoverRAC className={className}>
      <ListBoxRAC>
        {months.map((month, index) =>
          applyInternalProps<MonthOptionInternalProps>(optionElement, {
            key: String(index + 1),
            value: String(index + 1),
            label: month,
          }),
        )}
      </ListBoxRAC>
    </PopoverRAC>
  );
};

type MonthOptionProps = {
  className?: string;
};

type MonthOptionInternalProps = {
  value: string;
  label: string;
};

const MonthOption = ({ className, ...internal }: MonthOptionProps) => {
  const { value, label } = internal as MonthOptionInternalProps;
  return (
    <ListBoxItemRAC className={className} id={value} textValue={label}>
      {label}
    </ListBoxItemRAC>
  );
};

Month.Label = MonthLabel;
Month.Options = MonthOptions;
MonthOptions.Option = MonthOption;

type DayProps = {
  children: ReactNode;
  className?: string;
};

const Day = ({ children, className }: DayProps) => {
  const { disabled, showDateLabels } = useFieldPropsV2<DateInputProps>();
  const { partValue, partError, onPartChange } = useDatePart(
    SupportedDateParts.DAY,
  );

  const labelElement = findChildOfType(children, Day.Label);
  const inputElement = findChildOfType(children, Day.Input);

  return (
    <div className={className} data-date-field={true}>
      {showDateLabels && labelElement}
      <TextFieldRAC
        value={partValue}
        onChange={onPartChange}
        isDisabled={disabled}
        isInvalid={partError}
      >
        {inputElement}
      </TextFieldRAC>
    </div>
  );
};

type DayLabelProps = {
  className?: string;
};

const DayLabel = ({ className }: DayLabelProps) => {
  return <DateLabel datePart={SupportedDateParts.DAY} className={className} />;
};

type DayInputProps = {
  className?: string;
};

const DayInput = ({ className }: DayInputProps) => {
  const { id, hidePlaceholder } = useFieldPropsV2<DateInputProps>();
  const { inputId } = useDateFieldAttributes(SupportedDateParts.DAY);
  const { isFirstPart } = useDatePart(SupportedDateParts.DAY);
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const placeholder = hidePlaceholder
    ? undefined
    : t('field.date.placeholder.day');

  useFocusFieldEvent(() => {
    if (isFirstPart) {
      inputRef.current?.focus();
    }
  }, id);

  return (
    <InputRAC
      ref={inputRef}
      id={inputId}
      className={className}
      placeholder={placeholder}
      maxLength={2}
      inputMode="numeric"
      data-hook={SupportedDateParts.DAY}
    />
  );
};

Day.Label = DayLabel;
Day.Input = DayInput;

type YearProps = {
  children: ReactNode;
  className?: string;
};

const Year = ({ children, className }: YearProps) => {
  const { disabled, showDateLabels } = useFieldPropsV2<DateInputProps>();
  const { partValue, partError, onPartChange } = useDatePart(
    SupportedDateParts.YEAR,
  );

  const labelElement = findChildOfType(children, Year.Label);
  const inputElement = findChildOfType(children, Year.Input);

  return (
    <div className={className} data-date-field={true}>
      {showDateLabels && labelElement}
      <TextFieldRAC
        value={partValue}
        onChange={onPartChange}
        isDisabled={disabled}
        isInvalid={partError}
      >
        {inputElement}
      </TextFieldRAC>
    </div>
  );
};

type YearLabelProps = {
  className?: string;
};

const YearLabel = ({ className }: YearLabelProps) => {
  return <DateLabel datePart={SupportedDateParts.YEAR} className={className} />;
};

type YearInputProps = {
  className?: string;
};

const YearInput = ({ className }: YearInputProps) => {
  const { id, hidePlaceholder } = useFieldPropsV2<DateInputProps>();
  const { inputId } = useDateFieldAttributes(SupportedDateParts.YEAR);
  const { isFirstPart } = useDatePart(SupportedDateParts.YEAR);
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const placeholder = hidePlaceholder
    ? undefined
    : t('field.date.placeholder.year');

  useFocusFieldEvent(() => {
    if (isFirstPart) {
      inputRef.current?.focus();
    }
  }, id);

  return (
    <InputRAC
      ref={inputRef}
      id={inputId}
      className={className}
      placeholder={placeholder}
      maxLength={4}
      inputMode="numeric"
      data-hook={SupportedDateParts.YEAR}
    />
  );
};

Year.Label = YearLabel;
Year.Input = YearInput;

DateInput.Label = Label;
DateInput.Month = Month;
DateInput.Day = Day;
DateInput.Year = Year;
DateInput.Description = Description;
DateInput.Error = Error;

function useDateFieldAttributes(datePart: SupportedDateParts) {
  const { inputId } = useFieldAttributes();

  return useMemo(() => {
    const partName = datePart.toLowerCase();
    return {
      inputId: `${inputId}-${partName}-input`,
      labelId: `${inputId}-${partName}-label`,
    };
  }, [inputId, datePart]);
}

function useDatePart(datePart: SupportedDateParts) {
  const {
    value,
    onChange,
    hasError = false,
  } = useFieldPropsV2<DateInputProps>();
  const { regionalFormat } = useConfig();

  const dateValues = useMemo(() => deserializeDate(value ?? ''), [value]);
  const errors = useMemo(
    () => getDatePartsErrors(hasError, value ?? ''),
    [value, hasError],
  );

  const partValue = dateValues[datePart] || '';

  const onPartChange = useCallback(
    (newValue: string) => {
      const serializedValue = serializeDate(newValue, datePart, dateValues);
      onChange(serializedValue);
    },
    [onChange, dateValues, datePart],
  );

  const isFirstPart = useMemo(() => {
    const dateFormat = getDateFormatParts(regionalFormat)
      .filter(isSupportedDatePart)
      .map((part) => part.toUpperCase() as SupportedDateParts);
    const firstPart = dateFormat[0];
    return firstPart === datePart;
  }, [regionalFormat, datePart]);

  return {
    partValue,
    partError: errors[datePart],
    onPartChange,
    isFirstPart,
  };
}

type DateLabelProps = {
  datePart: SupportedDateParts;
  className?: string;
};

function DateLabel({ datePart, className }: DateLabelProps) {
  const { inputId, labelId } = useDateFieldAttributes(datePart);
  const { t } = useTranslation();

  const translationKey = `field.date.label.${datePart.toLowerCase()}`;
  const label = t(translationKey);

  return (
    <label id={labelId} htmlFor={inputId} className={className}>
      {label}
    </label>
  );
}
